/**
 * READ-BACKS AND SUMMARIES, as the composer sets them up (P-10.2). PURE.
 *
 * The sidecar writes both from the answers a Call SENDS (`tasks/prompt.ts` `renderReadBack`),
 * so the customer can never be told Wednesday while the booking says Tuesday. What the
 * composer decides is only WHICH Call a Yes/No reads back (`readBackOf`) and which Call a
 * message summarises (`summaryOf`), plus the short name each detail is given in them.
 */

const stepsOf = definition =>
  Array.isArray(definition?.steps) ? definition.steps : [];

/**
 * The Call a yes on this `choose` step runs straight away, through messages and other Calls
 * only (the sidecar's `confirmsCall`), or `''`.
 */
export const readBackTargetOf = (definition, stepId) => {
  const steps = stepsOf(definition);
  const byId = new Map(steps.map(step => [step.id, step]));
  const step = byId.get(stepId);
  if (step?.type !== 'choose') return '';
  const starts = (step.options ?? [])
    .map(option => option.next || step.next)
    .filter(Boolean);
  const seen = new Set();
  const queue = [...starts];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!seen.has(id)) {
      seen.add(id);
      const at = byId.get(id);
      if (at?.type === 'call') return at.id;
      if (at?.type === 'tell' && at.next) queue.push(at.next);
    }
  }
  return '';
};

/** The Calls this task reads back or summarises. */
const shownCalls = definition =>
  new Set(
    stepsOf(definition).flatMap(step => {
      if (step.type === 'choose' && step.readBackOf) return [step.readBackOf];
      if (step.type === 'tell' && step.summaryOf) return [step.summaryOf];
      return [];
    })
  );

/** Whether the answer saved under `key` is a line in a read-back or a summary. */
export const shownInReadBack = (definition, key) => {
  if (!key) return false;
  const calls = shownCalls(definition);
  return stepsOf(definition).some(
    step =>
      step.type === 'call' &&
      calls.has(step.id) &&
      Object.values(step.inputs ?? {}).some(source => source?.key === key)
  );
};

/** The nearest Call above row `index` in the list, a message's summary candidate, or `''`. */
export const summaryTargetOf = (rows, index) => {
  for (let at = index - 1; at >= 0; at -= 1) {
    const step = rows[at]?.step;
    if (step?.type === 'call') return step.id ?? '';
  }
  return '';
};

/** The chat keys a Call keeps from its answer (the booking reference), for a summary. */
export const keptKeysOf = (rows, callId) => {
  const call = rows.find(row => row.step?.id === callId)?.step;
  return call?.type === 'call' ? Object.values(call.keep ?? {}) : [];
};

// ── kept values with their own names (services task review E) ─────────────────────────
//
// A read-back lists what the Call it confirms SENDS. What an EARLIER Call kept (a quote's price)
// is not one of those, so a Yes/No names it in `readBackKeep`, and a message may name a kept value
// in `summaryKeep` the same way: `{ key, labelEn, labelAr, suffixEn?, suffixAr? }`, printed by the
// sidecar as "Amount: 40 KWD" / "المبلغ: ٤٠ د.ك". A `summaryKeep` entry may also be a bare key,
// as it always could. Every helper here keeps an entry it is not changing exactly as it was.

/** At most this many kept values under a read-back (`schema.ts` `MAX_READ_BACK_KEEP`). */
export const MAX_READ_BACK_KEEP = 3;
/** The words after a kept value (`schema.ts` `MAX_KEPT_SUFFIX`). */
export const MAX_KEPT_SUFFIX = 12;

/** The save key a kept entry names: a bare key, or a named entry's `key`. */
export const keptKeyOf = entry =>
  typeof entry === 'string' ? entry : (entry?.key ?? '');

/** Whether a `summaryKeep` or `readBackKeep` list shows `key`, in either form. */
export const keepsKey = (list, key) =>
  (Array.isArray(list) ? list : []).some(entry => keptKeyOf(entry) === key);

/**
 * `summaryKeep` with `key` ticked on or off. On adds a bare key, unless the list already shows it
 * in either form; off takes away every form of it. A named entry for another key is untouched.
 * `undefined` for an empty list, so the field comes off rather than saving `[]`.
 */
export const withSummaryKept = (list, key, on) => {
  const now = Array.isArray(list) ? list : [];
  let next;
  if (on) next = keepsKey(now, key) ? now : [...now, key];
  else next = now.filter(entry => keptKeyOf(entry) !== key);
  return next.length ? next : undefined;
};

/**
 * The keys a Yes/No may show under its read-back: what every OTHER Call of the task keeps (the
 * Call it confirms has not run yet when it asks), plus any it already shows, so a saved one can
 * always be unticked. In step order, once each.
 */
export const readBackKeepChoices = (definition, stepId) => {
  const step = stepsOf(definition).find(one => one.id === stepId);
  const target = step?.readBackOf ?? '';
  const kept = stepsOf(definition).flatMap(one =>
    one.type === 'call' && one.id !== target
      ? Object.values(one.keep ?? {})
      : []
  );
  const shown = (step?.readBackKeep ?? []).map(keptKeyOf);
  return [...new Set([...kept, ...shown])].filter(Boolean);
};

/**
 * `readBackKeep` with `key` ticked on or off. A new entry starts named after its key in English,
 * so it is never saved with no name at all (`schema.ts` refuses that); the owner writes the real
 * names in the boxes below it. Past the cap, on does nothing.
 */
export const withReadBackKept = (list, key, on) => {
  const now = Array.isArray(list) ? list : [];
  let next;
  if (!on) next = now.filter(entry => keptKeyOf(entry) !== key);
  else if (keepsKey(now, key) || now.length >= MAX_READ_BACK_KEEP) next = now;
  else next = [...now, { key, labelEn: key, labelAr: '' }];
  return next.length ? next : undefined;
};

/**
 * One named entry with one of its words changed (`labelEn`, `labelAr`, `suffixEn`, `suffixAr`).
 * A blank suffix comes off, as the sidecar reads blank and absent alike; a name stays a string.
 */
export const withKeptWords = (list, key, field, value) =>
  (Array.isArray(list) ? list : []).map(entry => {
    if (typeof entry === 'string' || entry?.key !== key) return entry;
    const next = { ...entry, [field]: value };
    if (field.startsWith('suffix') && value.trim() === '') delete next[field];
    return next;
  });
